import React, { useState, useMemo } from "react";
import SessionEventsPanel from "./SessionEventsPanel";
import {
  SOURCE_BADGE_CLASS,
  SOURCE_LABELS,
  describeActions,
  entityChips,
  formatDuration,
  parseTs,
  summarizeSessions,
} from "./sessionEventLabels";

const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100];

const SESSION_COLUMNS = [
  { key: "started_at", label: "Started", sortable: true },
  { key: "username", label: "User", sortable: true },
  { key: "website_name", label: "Brand", sortable: true },
  { key: "source", label: "Source", sortable: true },
  { key: "duration_ms", label: "Duration", sortable: true, numeric: true },
  { key: "page_views", label: "Pages", sortable: true, numeric: true },
  { key: "actions", label: "Actions", sortable: true, numeric: true },
  { key: "journey", label: "Journey", sortable: false },
];

const LEGACY_COLUMNS = [
  { key: "timestamp", label: "Time" },
  { key: "username", label: "User" },
  { key: "action", label: "Action" },
  { key: "page", label: "Page" },
  { key: "brand", label: "Brand" },
];

const SortIcon = ({ direction }) => (
  <svg className='inline-block ml-1 w-3 h-3' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    {direction === "asc" ? (
      <path strokeLinecap='round' strokeLinejoin='round' d='M5 15l7-7 7 7' />
    ) : (
      <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
    )}
  </svg>
);

const Chevron = ({ open }) => (
  <svg
    className={`w-3.5 h-3.5 text-text-tertiary transition-transform ${open ? "rotate-90" : ""}`}
    fill='none'
    viewBox='0 0 24 24'
    stroke='currentColor'
    strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M9 5l7 7-7 7' />
  </svg>
);

const formatTime = (ts) => {
  const d = parseTs(ts);
  if (!d) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Readable page names come from the backend (`pages`); fall back to raw paths.
const journeyText = (s) => {
  const pages = Array.isArray(s.pages) && s.pages.length ? s.pages : null;
  const parts = pages || (Array.isArray(s.surfaces) && s.surfaces.length ? s.surfaces : s.landing_page ? [s.landing_page] : []);
  return parts.join(" → ");
};

const CHIP_CLASS = {
  agent: "bg-accent-purple/10 text-accent-purple",
  run: "bg-accent-green/10 text-accent-green",
  scan: "bg-accent-green/10 text-accent-green",
  group: "bg-severity-medium/10 text-severity-medium",
  finding: "bg-severity-medium/10 text-severity-medium",
};
const CHIP_PREFIX = { agent: "Agent", run: "Run", scan: "Scan", group: "Group", finding: "Finding" };
const MAX_CHIPS = 4;

const rowSearchText = (s, entities) =>
  [
    s.username,
    s.user_email,
    s.website_name,
    SOURCE_LABELS[s.source] || s.source,
    journeyText(s),
    describeActions(s.action_counts),
    ...entityChips(s, entities).map((c) => c.label),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const compare = (a, b, col) => {
  if (col.numeric) return (Number(a[col.key]) || 0) - (Number(b[col.key]) || 0);
  if (col.key === "started_at") return (parseTs(a.started_at)?.getTime() || 0) - (parseTs(b.started_at)?.getTime() || 0);
  const va = (a[col.key] || "").toString().toLowerCase();
  const vb = (b[col.key] || "").toString().toLowerCase();
  return va < vb ? -1 : va > vb ? 1 : 0;
};

function pageRange(totalPages, current) {
  const pages = [];
  const maxVisible = 7;
  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }
  pages.push(1);
  let start = Math.max(2, current - 1);
  let end = Math.min(totalPages - 1, current + 1);
  if (current <= 3) { start = 2; end = 5; }
  if (current >= totalPages - 2) { start = totalPages - 4; end = totalPages - 1; }
  if (start > 2) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push("...");
  pages.push(totalPages);
  return pages;
}

const StatTile = ({ label, value, hint }) => (
  <div className='rounded-xl border border-border-subtle bg-surface-neutral/40 px-4 py-3'>
    <p className='text-[10px] font-semibold uppercase tracking-wider text-text-tertiary'>{label}</p>
    <p className='text-body-lg font-semibold text-text-primary mt-0.5'>{value}</p>
    {hint ? <p className='text-[10px] text-text-tertiary mt-0.5'>{hint}</p> : null}
  </div>
);

/**
 * BI "User Activity" — one row per session. Rows come from Console `/bi/user-activity`
 * (`kind: "sessions"`): Hub sessions (client session_id, 30-min idle rollover) plus
 * sessionized legacy V1 activity. Expanding a Hub row loads its raw event timeline.
 * Search / sort / paging are client-side over the loaded page (≤ 1000 newest sessions).
 */
const UserActivityTable = ({ data, isLoading, error, useProd = true }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("started_at");
  const [sortDir, setSortDir] = useState("desc");
  const [expanded, setExpanded] = useState(() => new Set());

  const isSessions = data?.kind === "sessions";
  const rows = useMemo(() => (Array.isArray(data?.activities) ? data.activities : []), [data]);
  const entities = useMemo(() => data?.entities || {}, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    if (!isSessions) {
      return rows.filter((a) =>
        [a.username, a.action, a.page, a.brand].some((v) => (v || "").toLowerCase().includes(q))
      );
    }
    return rows.filter((s) => rowSearchText(s, entities).includes(q));
  }, [rows, search, isSessions, entities]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    const col = (isSessions ? SESSION_COLUMNS : LEGACY_COLUMNS).find((c) => c.key === sortCol);
    if (!col) return filtered;
    const out = [...filtered].sort((a, b) => compare(a, b, col));
    return sortDir === "asc" ? out : out.reverse();
  }, [filtered, sortCol, sortDir, isSessions]);

  const summary = useMemo(() => (isSessions ? summarizeSessions(filtered) : null), [filtered, isSessions]);

  const totalFiltered = sorted.length;
  const totalPages = Math.ceil(totalFiltered / itemsPerPage);
  const safeCurrentPage = Math.min(currentPage, Math.max(totalPages, 1));
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const pageRows = sorted.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (col) => {
    if (!col.sortable && isSessions) return;
    if (sortCol === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col.key);
      setSortDir(col.numeric || col.key === "started_at" || col.key === "timestamp" ? "desc" : "asc");
    }
    setCurrentPage(1);
  };

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className='space-y-3'>
        <div className='h-5 bg-surface-neutral-subtle rounded w-32 mb-4' />
        {[...Array(8)].map((_, i) => (
          <div key={i} className='h-10 bg-surface-neutral rounded-lg animate-pulse' />
        ))}
      </div>
    );
  }

  if (error) {
    const detail = error?.data?.detail;
    return (
      <p className='text-body-sm text-severity-high py-8 text-center'>
        Failed to load user activity{detail ? ` — ${detail}` : ""}
      </p>
    );
  }

  if (!rows.length) {
    return <p className='text-body-sm text-text-tertiary py-8 text-center'>No activity in this window</p>;
  }

  const columns = isSessions ? SESSION_COLUMNS : LEGACY_COLUMNS;
  const totalCount = data?.total_count ?? rows.length;
  const truncated = isSessions && totalCount > rows.length;

  return (
    <div>
      {/* Header row */}
      <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
        <div>
          <h2 className='text-body-lg font-semibold text-text-primary'>{isSessions ? "Sessions" : "User Activity"}</h2>
          <p className='text-body-xs text-text-tertiary mt-0.5'>
            {isSessions
              ? "One row per Hub session (30-min idle rollover) or sessionized legacy activity. Expand a Hub row for its event timeline."
              : "Raw events (legacy view — Platform on this environment predates session rollups)."}
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <div className='relative'>
            <svg className='absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
            </svg>
            <input
              type='text'
              placeholder={isSessions ? "Search user, brand, page, action…" : "Search..."}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className='pl-8 pr-3 py-1.5 w-60 text-body-xs bg-surface-neutral rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-accent-purple/20 placeholder:text-text-tertiary'
            />
          </div>
          <select
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className='px-2 py-1.5 text-body-xs text-text-secondary bg-surface-neutral rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-accent-purple/20'>
            {ITEMS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
          <span className='text-body-xs text-text-tertiary whitespace-nowrap'>
            {totalFiltered === rows.length ? `${rows.length} loaded` : `${totalFiltered} of ${rows.length}`}
            {truncated ? ` · ${totalCount} total` : ""}
          </span>
        </div>
      </div>

      {truncated ? (
        <p className='text-body-xs text-severity-medium mb-3'>
          Showing the newest {rows.length} of {totalCount} sessions — narrow the date range or filters to see the rest.
        </p>
      ) : null}

      {summary ? (
        <div className='grid grid-cols-2 md:grid-cols-5 gap-3 mb-4'>
          <StatTile label='Sessions' value={summary.count} hint={`${summary.users} unique users`} />
          <StatTile label='Median duration' value={formatDuration(summary.medianDurationMs)} hint='Hub sessions only' />
          <StatTile label='Pages viewed' value={summary.pageViews} />
          <StatTile label='Actions taken' value={summary.totalActions} hint='done · discard · share · drill-in · assign' />
          <StatTile label='Sessions with an action' value={`${Math.round(summary.actionRate * 100)}%`} hint={`${summary.withActions} of ${summary.count}`} />
        </div>
      ) : null}

      {/* Table */}
      <div className='overflow-x-auto rounded-xl border border-border-subtle'>
        <table className='w-full'>
          <thead>
            <tr className='bg-surface-neutral'>
              {isSessions ? <th className='w-8' /> : null}
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col)}
                  className={`px-4 py-2.5 text-left text-body-xs font-semibold text-text-secondary select-none transition-colors ${
                    col.sortable === false ? "" : "cursor-pointer hover:text-text-primary"
                  } ${col.numeric ? "text-right" : ""}`}>
                  {col.label}
                  {sortCol === col.key && <SortIcon direction={sortDir} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-border-subtle'>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className='px-4 py-8 text-center text-body-sm text-text-tertiary'>
                  No results match "{search}"
                </td>
              </tr>
            ) : isSessions ? (
              pageRows.map((s) => {
                const open = expanded.has(s.session_id);
                const actionsText = describeActions(s.action_counts);
                const chips = entityChips(s, entities);
                return (
                  <React.Fragment key={s.session_id}>
                    <tr
                      onClick={() => toggleExpanded(s.session_id)}
                      className='hover:bg-surface-neutral/50 transition-colors cursor-pointer'>
                      <td className='pl-3'><Chevron open={open} /></td>
                      <td className='px-4 py-2.5 text-body-xs text-text-secondary whitespace-nowrap'>{formatTime(s.started_at)}</td>
                      <td className='px-4 py-2.5 text-body-sm font-medium text-text-primary' title={s.user_email || ""}>{s.username || s.user_id || "—"}</td>
                      <td className='px-4 py-2.5 text-body-xs text-text-secondary'>{s.website_name || (s.website_id ? s.website_id : "—")}</td>
                      <td className='px-4 py-2.5'>
                        <span className={`inline-block px-2 py-0.5 rounded-md text-body-xs font-medium ${SOURCE_BADGE_CLASS[s.source] || "bg-surface-neutral-subtle text-text-secondary"}`}>
                          {SOURCE_LABELS[s.source] || s.source}
                        </span>
                        {s.login_count > 0 && s.source === "hub_v2" ? (
                          <span className='ml-1 text-[10px] text-text-tertiary'>+login</span>
                        ) : null}
                      </td>
                      <td className='px-4 py-2.5 text-body-xs text-text-secondary text-right whitespace-nowrap'>
                        {s.source === "hub_v2" ? formatDuration(s.duration_ms) : "—"}
                      </td>
                      <td className='px-4 py-2.5 text-body-xs text-text-secondary text-right'>{s.page_views ?? 0}</td>
                      <td className='px-4 py-2.5 text-right'>
                        {s.actions > 0 ? (
                          <span className='inline-block px-2 py-0.5 rounded-md text-body-xs font-medium bg-severity-medium/10 text-severity-medium' title={actionsText}>
                            {s.actions}
                          </span>
                        ) : (
                          <span className='text-body-xs text-text-tertiary'>0</span>
                        )}
                      </td>
                      <td className='px-4 py-2.5 text-body-xs text-text-secondary max-w-[420px]'>
                        <span className='block truncate' title={journeyText(s)}>{journeyText(s) || "—"}</span>
                        {chips.length ? (
                          <span className='flex flex-wrap gap-1 mt-1'>
                            {chips.slice(0, MAX_CHIPS).map((c) => {
                              const cls = `inline-block max-w-[180px] truncate px-1.5 py-0.5 rounded-md text-[10px] font-medium ${CHIP_CLASS[c.kind]}`;
                              const title = `${CHIP_PREFIX[c.kind]}: ${c.label} (${c.id})${c.href ? " — open on Hub" : ""}`;
                              return c.href ? (
                                <a
                                  key={`${c.kind}-${c.id}`}
                                  href={c.href}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  title={title}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`${cls} underline decoration-dotted underline-offset-2 hover:decoration-solid`}>
                                  {CHIP_PREFIX[c.kind]} · {c.label} ↗
                                </a>
                              ) : (
                                <span key={`${c.kind}-${c.id}`} title={title} className={cls}>
                                  {CHIP_PREFIX[c.kind]} · {c.label}
                                </span>
                              );
                            })}
                            {chips.length > MAX_CHIPS ? (
                              <span className='text-[10px] text-text-tertiary'>+{chips.length - MAX_CHIPS} more</span>
                            ) : null}
                          </span>
                        ) : null}
                        {actionsText ? <span className='block text-[10px] text-severity-medium truncate mt-0.5'>{actionsText}</span> : null}
                      </td>
                    </tr>
                    {open ? (
                      <tr className='bg-surface-neutral/30'>
                        <td />
                        <td colSpan={columns.length} className='p-0'>
                          <SessionEventsPanel session={s} useProd={useProd} hubUrl={data?.hub_url || null} />
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            ) : (
              pageRows.map((a, i) => (
                <tr key={startIndex + i} className='hover:bg-surface-neutral/50 transition-colors'>
                  <td className='px-4 py-2.5 text-body-xs text-text-secondary whitespace-nowrap'>{formatTime(a.timestamp)}</td>
                  <td className='px-4 py-2.5 text-body-sm font-medium text-text-primary'>{a.username || "—"}</td>
                  <td className='px-4 py-2.5'>
                    <span className='inline-block px-2 py-0.5 rounded-md text-body-xs font-medium bg-surface-neutral-subtle text-text-secondary'>{a.action}</span>
                  </td>
                  <td className='px-4 py-2.5 text-body-xs text-text-secondary'>{a.page}</td>
                  <td className='px-4 py-2.5 text-body-xs text-text-secondary'>{a.brand || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between mt-4'>
          <span className='text-body-xs text-text-tertiary'>
            {startIndex + 1}–{Math.min(startIndex + itemsPerPage, totalFiltered)} of {totalFiltered}
          </span>
          <div className='flex items-center gap-1'>
            <button
              onClick={() => setCurrentPage(safeCurrentPage - 1)}
              disabled={safeCurrentPage === 1}
              className='px-2.5 py-1.5 text-body-xs font-medium text-text-secondary rounded-lg hover:bg-surface-neutral disabled:opacity-30 disabled:cursor-not-allowed transition-colors'>
              Prev
            </button>
            {pageRange(totalPages, safeCurrentPage).map((p, i) =>
              p === "..." ? (
                <span key={`e${i}`} className='w-8 text-center text-body-xs text-text-tertiary'>...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-8 h-8 text-body-xs font-medium rounded-lg transition-colors ${
                    p === safeCurrentPage ? "bg-button-primary text-white" : "text-text-secondary hover:bg-surface-neutral"
                  }`}>
                  {p}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage(safeCurrentPage + 1)}
              disabled={safeCurrentPage === totalPages}
              className='px-2.5 py-1.5 text-body-xs font-medium text-text-secondary rounded-lg hover:bg-surface-neutral disabled:opacity-30 disabled:cursor-not-allowed transition-colors'>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserActivityTable;
